import { ISiteVisit, ISiteVisitQuestion, SiteVisitQuestionType, PolyResult } from '../types';

/**
 * Utility class for sanitizing SiteVisit data from GraphQL responses
 * Removes null/undefined enum fields and empty results to prevent validation errors
 */
export class SiteVisitDataSanitizer {
  private static isEmptyResult(result: unknown): boolean {
    if (!result || result === null || result === undefined) return true;

    if (typeof result === 'object') {
      if (Array.isArray(result)) return result.length === 0;
      return Object.keys(result).length === 0 || Object.values(result).every(val => !val);
    }

    if (typeof result === 'string') return result.trim() === '';
    if (typeof result === 'number') return result === 0;

    return false;
  }

  private static getTypeSpecificFields(
    rawQuestion: Record<string, unknown>
  ): Record<string, unknown> {
    const fields: Record<string, unknown> = {};

    switch (rawQuestion.type) {
      case SiteVisitQuestionType.map:
        if (this.isValidEnumValue(rawQuestion.mapType)) {
          fields.mapType = rawQuestion.mapType;
        }
        if (this.isValidObject(rawQuestion.colors)) {
          fields.colors = rawQuestion.colors;
        }
        break;

      case SiteVisitQuestionType.select:
        if (this.isValidEnumValue(rawQuestion.mode)) {
          fields.mode = rawQuestion.mode;
        }
        if (this.isValidArray(rawQuestion.options)) {
          fields.options = rawQuestion.options;
        }
        break;
    }

    return fields;
  }

  private static isValidEnumValue(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private static isValidObject(value: unknown): boolean {
    return value != null && typeof value === 'object' && !Array.isArray(value);
  }

  /**
   * Validates if a value is a valid non-empty array
   */
  private static isValidArray(value: unknown): boolean {
    return Array.isArray(value) && value.length > 0;
  }

  private static sanitizeQuestion(rawQuestion: Record<string, unknown>): ISiteVisitQuestion {
    if (!rawQuestion || !rawQuestion.type) {
      throw new Error('Invalid question data: type is required');
    }

    const baseQuestion = {
      id: rawQuestion.id,
      title: rawQuestion.title,
      type: rawQuestion.type,
      category: rawQuestion.category,
      required: Boolean(rawQuestion.required),
    };

    // Add optional fields only if they have meaningful values
    const optionalFields: Partial<ISiteVisitQuestion> = {};
    if (rawQuestion.description && typeof rawQuestion.description === 'string') {
      optionalFields.description = rawQuestion.description;
    }
    if (rawQuestion.help && typeof rawQuestion.help === 'string') {
      optionalFields.help = rawQuestion.help;
    }
    if (this.isValidArray(rawQuestion.helpImages)) {
      optionalFields.helpImages = rawQuestion.helpImages as string[];
    }
    if (rawQuestion.result && !this.isEmptyResult(rawQuestion.result)) {
      optionalFields.result = rawQuestion.result as PolyResult; // Will be properly typed by the specific question interfaces
    }

    // Add type-specific fields
    const typeSpecificFields = this.getTypeSpecificFields(rawQuestion);

    return { ...baseQuestion, ...optionalFields, ...typeSpecificFields } as ISiteVisitQuestion;
  }

  private static sanitizeCategory(rawCategory: Record<string, unknown>) {
    if (!rawCategory || !rawCategory.questions) {
      throw new Error('Invalid category data: questions array is required');
    }

    const result: Record<string, unknown> = {
      id: rawCategory.id,
      title: rawCategory.title,
      type: rawCategory.type,
      icon: rawCategory.icon,
      questions: Array.isArray(rawCategory.questions)
        ? (rawCategory.questions as Record<string, unknown>[]).map(this.sanitizeQuestion.bind(this))
        : [],
    };

    if (rawCategory.description && typeof rawCategory.description === 'string') {
      result.description = rawCategory.description;
    }

    return result;
  }

  public static sanitize(rawSiteVisit: Record<string, unknown>): ISiteVisit {
    if (!rawSiteVisit) {
      throw new Error('SiteVisit data is required for sanitization');
    }

    if (!rawSiteVisit.categories || !Array.isArray(rawSiteVisit.categories)) {
      throw new Error('Invalid SiteVisit structure: categories array is required');
    }

    try {
      return {
        ...rawSiteVisit,
        categories: Array.isArray(rawSiteVisit.categories)
          ? (rawSiteVisit.categories as Record<string, unknown>[]).map(
              this.sanitizeCategory.bind(this)
            )
          : [],
      } as unknown as ISiteVisit;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown sanitization error';
      throw new Error(`SiteVisit sanitization failed: ${message}`);
    }
  }
}

export const sanitizeSiteVisitData = (rawSiteVisit: Record<string, unknown>): ISiteVisit => {
  return SiteVisitDataSanitizer.sanitize(rawSiteVisit);
};
